import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

/**
 * Standardized Floating Bottom Action Bar for all application forms
 */
const FloatingFormActions = ({
  isDirty,
  statusBadge,
  prevStep,
  onPrev,
  nextStep,
  onNext,
  onCancel,
  cancelUrl,
  cancelText = 'Discard',
  onSubmit,
  saveText = 'Save Changes',
  loading = false,
  disabled = false,
  saveIcon = 'solar:check-read-linear',
  extraLeft,
  extraRight,
  isSubmitButton = true,
  hideSave = false,
}) => {
  const navigate = useNavigate();

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (cancelUrl) {
      navigate(cancelUrl);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="floating-form-actions">
      <div className="container-fluid d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
        {/* Left Side: Status, Changes & Step Navigation */}
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* Unsaved / Saved Badge */}
          {typeof isDirty === 'boolean' && (
            isDirty ? (
              <span className="badge bg-warning-50 text-warning-700 border border-warning-200 px-10 py-6 radius-6 text-xs d-flex align-items-center gap-1">
                <Icon icon="solar:danger-circle-bold" className="text-sm" /> Unsaved Changes
              </span>
            ) : (
              <span className="badge bg-success-50 text-success-700 border border-success-200 px-10 py-6 radius-6 text-xs d-flex align-items-center gap-1">
                <Icon icon="solar:check-circle-bold" className="text-sm" /> All Saved
              </span>
            )
          )}

          {/* Custom Status Badge */}
          {statusBadge}

          {/* Step / Tab Navigation */}
          {(prevStep || nextStep) && (
            <div className="d-flex align-items-center gap-2">
              {prevStep && onPrev && (
                <button
                  type="button"
                  onClick={onPrev}
                  className="btn btn-sm btn-outline-secondary px-12 py-6 radius-6 text-xs d-inline-flex align-items-center gap-1"
                >
                  <Icon icon="solar:arrow-left-linear" />
                  <span>Prev: {prevStep}</span>
                </button>
              )}
              {nextStep && onNext && (
                <button
                  type="button"
                  onClick={onNext}
                  className="btn btn-sm btn-outline-secondary px-12 py-6 radius-6 text-xs d-inline-flex align-items-center gap-1"
                >
                  <span>Next: {nextStep}</span>
                  <Icon icon="solar:arrow-right-linear" />
                </button>
              )}
            </div>
          )}

          {extraLeft}
        </div>

        {/* Right Side: Discard & Save Actions */}
        <div className="d-flex align-items-center gap-3">
          {extraRight}

          <button
            type="button"
            className="btn btn-outline-danger px-20 py-8 radius-8 text-sm fw-semibold"
            onClick={handleCancel}
          >
            {cancelText}
          </button>

          {!hideSave && (
            <button
              type={isSubmitButton ? 'submit' : 'button'}
              onClick={(e) => {
                if (onSubmit) onSubmit(e);
              }}
              disabled={loading || disabled}
              className="btn btn-primary-600 px-28 py-8 radius-8 text-sm fw-semibold d-flex align-items-center gap-2 shadow-sm"
            >
              {loading ? (
                <Icon icon="line-md:loading-twotone-loop" className="text-base" />
              ) : (
                <Icon icon={saveIcon} className="text-base" />
              )}
              <span>{saveText}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloatingFormActions;
